export const DOCUMENTATION_DATA = {
  about: {
    title: 'About the Platform',
    description:
      '**GAINSS** is a test management solution designed to streamline software testing. From test planning and execution to automation and reporting, GAINSS enhances efficiency and ensures seamless collaboration.',

    sections: [
      {
        heading: 'Why GAINSS?',
        points: [
          '**Effortless Test Management** – Plan, execute, and track test cases efficiently.',
          '**Integrated Workflow** – Seamlessly link test cases with Jira for better traceability.',
          '**Automation-Ready** – Reduce manual efforts with built-in automation capabilities.',
          '**Real-Time Insights** – Monitor progress and ensure test coverage with detailed reports.',
          '**Scalable & Customizable** – Adapt GAINSS to your specific testing needs.',
        ],
      },
      {
        heading: 'Key Features',
        points: [
          '**Test Case Organization** – Create, manage, and reuse test cases efficiently.',
          '**Defect Tracking** – Identify, log, and resolve issues faster with built-in reporting.',
          '**Test Execution & Metrics** – Gain insights through dashboards and analytics.',
          '**Collaboration Tools** – Assign tasks, track updates, and improve team coordination.',
        ],
      },
      {
        heading: '',
        content: `Our documentation is **continuously updated** to provide the latest information.
          Whether you're new to GAINSS or an experienced user, this guide helps you maximize its potential.
        We’re here to support your testing journey every step of the way.`,
      },
    ],
  },

  register: {
    title: 'Registration',

    note: '**Note**: Registration on GAINSS is only available to organization administrators. Regular users cannot sign up directly; they must be invited by an administrator to join the platform.',

    sections: [
      {
        heading: 'How to Register as an Organization Admin',
        steps: [
          'Go to the <a href="/register">**GAINSS Registration Page**</a>.',
          'Fill out the required details and click **Create Account**.',
          'Check your inbox for a **confirmation email** from GAINSS.',
          'Click the **verification link** in the email to activate your account.',
          'Once verified, log in to GAINSS using your registered credentials.',
        ],
        image: '/gainss-registration.png',
      },
      {
        heading: 'Next Steps After Registration',
        paragraphs: [
          'After successfully registering and logging in, you can begin setting up your organization’s workspace. This includes adding team members, configuring projects, and integrating third-party tools such as Jira.',
          'To invite other users, navigate to the **Team** section and send invitations to team members.',
        ],
      },
    ],
  },

  'invite-members': {
    title: 'Inviting Team Members',
    note: '**Note**: Only organization admin and managers can invite new members to GAINSS. Users must be invited before they can access the platform.',
    sections: [
      {
        heading: 'How to Invite a Team Member',
          steps: [
            'Navigate to the **Team** page in the organization dashboard.',
            'Click on **Invite Member**.',
            'Enter the **email address, name, and role** of the team member.',
            'Select the **project(s)** to assign the user.',
            'Optionally, assign **Project Admin** access for selected projects.',
            'Click **Send Invite**.',
            'The invited user will receive an **email with instructions** on how to join the organization.',
            'Once the invite is accepted and the account setup is complete, the user can access the assigned projects on the platform.',
          ],
        image: '/invite-members-form.png',
      },
      {
        heading: 'Invite in Bulk',
        steps: [
          'Go to the **Team** page and click **Invite Member** > **Bulk Invite**.',
          'Download the sample CSV to see the required format.',
          'Prepare your CSV with **first name, last name, email, role**.',
          'Upload the file using **Upload CSV File** and click **Invite Users** to send invitations. ',
          'You’ll see a summary of successful and failed invites.',
          'Click **Try Again** to re-send invite or **Invite More Users** to start a new batch.',
        ],
        image: '/invite-team-bulk.png',
        note: 'Max 10 users can be invited at a time. Emails must be unique and roles must match predefined values (e.g., "Team Member").',
      },
      {
        heading: 'Next Steps After Invitation',
        paragraphs: [
          'Once team members accept the invitation, they will be added to the organization’s workspace.',
          'Administrators can manage users by assigning roles, updating permissions, or removing users if necessary.',
        ],
        image: '/accept-invite.png',
      },
    ],
  },

  'managing-team': {
    title: 'Managing Team Members',

    note: '**Note**: Organization admins have full control over user management, including assigning roles, deactivating, reactivating, or permanently removing users.',
    image: '/team-management.png',
    sections: [
      {
        heading: 'Managing Roles',
        steps: [
          'Navigate to the **Team** page.',
          'In the actions menu of a user, click on **Update Role**.',
          'Select the new role from the dropdown menu and click **Confirm**.',
          'Changes take effect immediately, updating their permissions accordingly.',
        ],
      },
      {
        heading: 'Deactivating & Reactivating Team Members',
        points: [
          'To **deactivate** a user, go to the **Team** page, select the member, and click **Deactivate User**.',
          'Deactivated users will **lose access** to the platform but can be **reactivated** at any time.',
          'To **reactivate**, find the deactivated user and click **Reactivate User** to restore access.',
        ],
        image: '/team-member-delete.png',
      },
      {
        heading: 'Permanently Deleting Team Members',
        points: [
          'To delete a user, navigate to **Team** and click on **Show Inactive**.',
          'Select the user you want to remove and click **Delete**.',
          '**Warning**: This action is **irreversible**, and the user will be permanently removed from the system.',
        ],
      },
    ],
  },

  dashboard: {
    title: 'Dashboard',
    description: `The Dashboard provides an overview for monitoring progress and recent activities across multiple projects. 
    It helps teams stay informed about ongoing test efforts, assigned tasks, and overall project status. 
    `,
    image: '/org-dashboard.png',
    sections: [
      {
        heading: '',
        paragraphs: [
          `Users can quickly navigate to their assigned test cases, track recent project changes, and review pending approvals, 
        ensuring a seamless workflow for test execution and validation.`,
        ],
      },
      {
        heading: 'Key Features',
        points: [
          '**Recent Projects** – Access and manage active projects, with recently updated ones prioritized.',
          '**Activity in the Last 24 Hours** – View test cases added, test cycles executed, and issues reported.',
          '**Assigned for Execution** – Track test cases assigned for execution along with related test cycles.',
          '**Assigned for Review** – Review and approve test cases directly from the dashboard.',
        ],
      },
    ],
  },

  'manage-projects': {
    title: 'View All Projects',
    description:
      'This page shows all the projects you have access to. Admins can see all created projects, and users can see the ones assigned to them.',
    sections: [
      {
        heading: 'For Users:',
        points: [
          'View all projects to which you have been assigned.',
          'Access and review relevant test cases, test cycles, and associated issues.',
        ],
        image: '/org-projects.png',
      },
      {
        heading: 'For Admins:',
        points: [
          'Edit project details, including project name and description.',
          'Delete projects that are no longer required.',
          'Manage and configure project workflows.',
          'Access and review all test cases, test suites, and test cycles within the projects.',
        ],
      },
    ],
    bottomNote:
      '**Note**: Deleting a project is a permanent action and cannot be reversed.',
  },

  'add-project': {
    title: 'Adding a New Project',
    note: '**Note**: Only organization admins and managers can create new projects in GAINSS.',
    sections: [
      {
        heading: 'How to Add a Project',
        steps: [
          'Navigate to the **Projects** section from the organization dashboard.',
          'Click on **Add New Project**.',
          'Enter the **Project Name** and provide a brief **Description**.',
          'Choose whether to enable **test case review workflow** by selecting **Yes** or **No**.',
          'Choose whether to enforce **mandatory assignment of test cases for execution**  within a test cycle.',
          'Select a **Default Assignee** for unassigned test cases (optional).',
          'Click **Create Project** to save.',
        ],
        image: '/add-project.png',
      },
      {
        heading: 'Next Steps After Adding a Project',
        paragraphs: [
          'Once the project is created, you can begin adding test cases, cycles, and assigning team members.',
          'Project settings can be updated at any time, including workflows and default behaviors.',
          'To learn more about configuring workflows, check out the <u>Project Workflows</u> section',
        ],
      },
    ],
  },

  'project-workflows': {
    title: 'Project Workflows',
    sections: [
      {
        heading: 'Test Case Review Workflow',
        paragraphs: [
          'This workflow ensures that test cases are reviewed before being assigned to a test cycle.',
          `You have the option to enable or disable this workflow based on the project’s specific requirements.`,
        ],
        points: [
          '**Enable Test Case Review Workflow:** Select "Yes" to require a review process for all test cases to be reviewed and approved by a designated reviewer, such as a project admin or team member, before being executed.',
          '**Disable Test Case Review Workflow:** Select "No" if the review process is not required. Test cases can be immediately marked as "Approved" and added to a test cycle without a formal review.',
        ],
      },
      {
        heading: 'Mandatory Assignment of Test Cases',
        paragraphs: [
          'his setting ensures that all test cases in a test cycle are assigned to a user for execution.',
          `You can choose to enable or disable this workflow depending on your team’s process for managing test executions.`,
        ],
        points: [
          '**Enable Mandatory Assignment:** Select "Yes" to require that every test case within a test cycle be assigned to a user. Only the assigned user will be able to execute the test case.',
          '**Disable Mandatory Assignment:** Select "No" to allow test cases in a test cycle to remain unassigned, with a default assignee automatically designated for execution.',
        ],
      },
      {
        heading: 'Default Assignee',
        paragraphs: [
          'The default assignee is automatically assigned to any test cases that remain unassigned within a test cycle. This feature helps ensure that no test case is left unassigned.',
          'You can specify a default assignee by selecting a user from the list of available team members.',
        ],
      },
    ],
  },

  'project-team': {
    title: 'Project Team',
    image: '/project-team.png',
    sections: [
      {
        heading: 'Managing Team Members in a Project',
        paragraphs: [
          'On the Project Team page, the organization admin, managers and project admins can manage team members assigned to a project. This includes:',
        ],
        points: [
          'Adding new users to the project team.',
          'Removing users from the project team.',
          'Updating user roles within the project.',
        ],
      },
      {
        heading: 'Assigning Users to a Project',
        paragraphs: [
          'To assign a new team member to a project, follow the steps below:',
        ],
        steps: [
          'Click on the "Add People" button on the Project Team page.',
          'Choose the role for the user from the available options (e.g., Member, Admin).',
          'Select from the existing list of users in your organization who can be assigned to that role.',
          'Click "Assign" to complete the process. The user will now have access to the project with the assigned role.',
        ],
      },
      {
        heading: 'Removing Users from a Project',
        paragraphs: ['To remove a user from a project, follow these steps:'],
        steps: [
          'Locate the user in the team list on the Project Team page.',
          'Click on the "Remove" option next to the user’s name.',
          'Confirm the removal. The user will no longer have access to the project.',
        ],
      },
      {
        heading: 'Roles and Permissions',
        paragraphs: [
          'Each user in the project team can be assigned a specific role, which dictates their level of access and permissions within the project. For details, refer to the <a href="/documentation?view=roles-permissions"><u>Roles and Permissions</u></a> section.',
        ],
      },
    ],
  },
  'test-automation': {
    title: 'Test Automation Metrics',

    image: '/test-accordion-header.png',

    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'The Test Automation section displays a consolidated table of automation metrics across all projects in the organization.'
        ],
        note: '**Note**: This section is visible only to organization admins, managers, executive manager, and project admins.'
      },

      {
        heading: 'Project-Level Metrics',
        paragraphs: [
          'Each row represents a project and displays key automation details such as Total Test Cases, Candidates for Automation, Automated Count, Completion Percentage, and status distribution.',
          'A dropdown is provided to switch between **Country-wise** and **Module-wise** views.',
          'Clicking the "+" icon on a project row displays detailed automation data based on the selected view.'
        ]
      },

      {
        heading: 'Expanded View (Country / Module Breakdown)',
        image: '/test-accordion-countrywise.png',
        paragraphs: [
          'Based on the selected dropdown option, expanding a project shows either country-wise or module-wise automation metrics.',
          'Rows are dynamically generated according to the selected grouping and the data available within the project.',
          'Test cases without an assigned country are grouped under "No Country" in the country-wise view.'
        ],
      }
    ],
    bottomNote:
      'Automation metrics can be downloaded in **Excel** or **PDF** format. Displayed data depends on the selected view and project configuration.'
  },


  'defect-overview': {
    title: 'Defect Overview',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'The Defect Overview section provides a consolidated view of defect metrics across one or more projects.',
          'Users can select projects using the dropdown and analyze defect trends through two visual reports: Defect Distribution and Defect Aging.'
        ]
      },
      {
        heading: 'Defect Distribution',
        image: '/defect-overview.png',
        paragraphs: [
          'The Defect Distribution report provides a high-level summary of defects across the selected projects.',
          'Summary cards display key metrics, including **Total Defects**, **Open Defects** and **Reopened Defects**.',
          'A bar chart visualizes the number of defects for each project, enabling quick comparison across projects.'
        ]
      },
      {
        heading: 'Defect Aging',
        image: '/defect-project-dropdown.png',
        paragraphs: [
          'The Defect Aging report categorizes defects based on how long they have remained open.',
          'Defects are grouped into predefined aging buckets, and a bar chart displays the count for each category.',
          'This view helps identify long-standing defects that may require immediate attention.'
        ]
      },
    ],
    bottomNote:
      'Defect data from this section can be downloaded in **Excel** or **PDF** format for offline analysis and reporting.'
  },


  'manage-configurations': {
    title: 'Manage Configurations',
    note: '**Note**: You must be an organization admin, manager or project admin to manage the configuration settings for your project. Team members do not have access to modify these settings.',
    image: '/configurations-page.png',
    sections: [
      {
        heading: 'Accessing Configuration Settings',
        paragraphs: [
          'Follow these steps to access the configuration settings for your project:',
        ],
        steps: [
          'Navigate to the project for which you want to modify configurations.',
          'In the project sidebar, click on "Configuration" to access the configuration settings page.',
          'Once in the configuration settings, you can customize various aspects of the project.',
        ],
      },
      {
        heading: 'Managing Existing Configurations',
        paragraphs: [
          'After adding configurations, you can manage them by editing or deleting entries from the list. To do this, simply locate the configuration item and use the available actions.',
          'GAINSS provides a few pre-added environments, priorities and statuses that cannot be deleted. You can add your own.',
        ],
      },
      {
        heading: 'Customize General Configurations',
        paragraphs: [
          'To customize general configurations such as Environments, Labels, Iterations, or Modules:',
        ],
        steps: [
          'Select one of the following configuration options: Environments, Labels, Priority, Modules or Automation config.',
          'In the selected configuration section, enter a name for the new entry.',
          'Once you\'ve entered the necessary information, click on "Add New" to create a new entry.',
        ],
        tip: 'You can optionally add a description when creating a new Environment, Module, or Iteration. This can help provide additional context or details.',
        note: 'Your new configuration entry will appear in the list below. You can edit or delete these entries as needed.',
      },
      {
        heading: 'Customize Priorities and Statuses',
        steps: [
          'Go to the Relevant section.',
          'Enter a name. You can also enter a description (optional).',
          'Select a color to represent the value (recommended).',
          'Click "Add".',
        ],
      },
    ],
    bottomNote:
      '**Note**: GAINSS will set the default priority to "Medium" and the default status to "Draft" automatically when creating a new test case. You can change it.',
  },

  'execution-scope': {
    title: 'Platforms & Coverage',
    image: '/platform-coverage.png',
    sections: [
      {
        heading: 'Testing Platforms',
        paragraphs: [
          'Testing platforms can be selected within a Test Suite or Test Cycle to improve test coverage and efficiency. They define the environments where your tests run, ensuring your application works across different systems, devices, and browsers.',
          'Choose from a variety of platforms that support different operating systems, browsers, and devices.',
        ],
      },
      {
        heading: 'Platforms & Supported Options',
        paragraphs: [
          'These include platforms and operating systems your application will be tested on. These can be configured within the Test Suite or Test Cycle to specify the required platform for testing.',
        ],
      },
      {
        heading: 'Supported Browsers',
        paragraphs: [
          'In addition to selecting the platforms, you can specify which browsers should be used for testing within the selected platforms. This ensures that your web application works across the most popular browsers and versions used by your audience.',
          'Supported Browsers include Chrome, Firefox, Safari, and Edge. By selecting specific browsers, you can narrow down your testing scope and ensure compatibility with the target audience.',
        ],
      },
      {
        heading: 'Support for Multiple Languages',
        paragraphs: [
          `You can enable support for multiple languages. This ensures that your application’s UI and functionality are tested in different languages.`,
          'Select the languages supported by your project and specify them in the configurations for more comprehensive testing.',
        ],
      },
      {
        heading: 'Support for Multiple Geographies',
        paragraphs: [
          'In addition to languages, you can select multiple geographies (regions) to test your project. This is particularly useful for verifying the compatibility of your application with different region-specific data, settings, and behaviors. Geographies can account for localization and time zone differences that may affect the user experience.',
        ],
      },
      {
        heading: 'Cloud Device Labs',
        paragraphs: [
          'Cloud Device Labs enable you to test your mobile applications or web applications on real devices remotely. You can select the Cloud Device Labs you want to ensure your project functions across various hardware configurations.',
        ],
      },
    ],
  },

  'jira-integration': {
    title: 'Jira Integration',
    image: '/jira-config.png',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'GAINSS allows you to integrate Jira with your projects, enabling better traceability between your test cases, test cycles, and Jira issues.',
          'You can link up to 5 Jira projects to a single GAINSS project, making it easy to associate testing activities with development tasks across multiple projects.',
        ],
      },
      {
        heading: 'Key Benefits',
        points: [
          'Easily associate test cases and test cycles with Jira issues for end-to-end traceability.',
          'Track test execution results directly linked to Jira stories, tasks, and bugs.',
          'Support for linking multiple Jira projects provides flexibility for complex development environments.',
        ],
      },
      {
        heading: 'Linking Jira Projects',
        steps: [
          'Navigate to the GAINSS project where you want to enable Jira integration.',
          'In the project sidebar, click on **Configurations** > **Integrations** and select **Project Management**.',
          'Click **Connect to Jira**.',
          'Follow the prompts to log in to your Jira account and authorize GAINSS to access your Jira projects.',
          'Select the JIRA site you want to connect to from the dropdown.',
          'Choose the Jira projects you want to  link to your GAINSS project.',
          'Click **Submit** to complete the integration.',
          'Once linked, you can associate Jira issues with your test cases and executions.',
        ],
      },
      {
        heading: 'Managing Linked Projects',
        steps: [
          'You can view all Jira projects linked to your GAINSS project from the Integrations > Project Management page.',
          'To unlink a Jira project or link another project, click on the **Modify Projects** button.',
          'Select or deselect the Jira projects you want to link or unlink from the list.',
          '<em>Warning:</em> Unlinking a project will remove any associations between your GAINSS items and Jira issues from that specific project.',
        ],
      },
      {
        heading: 'Syncing Linked Jira Issues',
        steps: [
          'Use the **Sync Linked Jira Issues** feature when issues have been moved to a different Jira project after they were initially linked in GAINSS.',
          'Go to **Configurations > Project Management** and click the **Sync Jira Issues** button to begin.',
          'This updates linked issues in GAINSS to reflect their latest state in Jira, if they have been moved.',
          '**IMPORTANT:** Ensure the Jira project containing the migrated issues is already linked to your GAINSS project before syncing.',
          'After syncing, you’ll see a summary of the updates, including how many issues were updated and their keys.',
        ],
      },
    ],
    bottomNote:
      '**Note**: Only organization admins, managers and project admins can manage Jira integration settings.',
  },

  'roles-permissions': {
    title: 'User Roles and Permissions',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'GAINSS uses role-based access control to manage what users can view and edit within a project.',
          'Each user is assigned a role that defines their permissions and level of access.',
        ],
      },
      {
        heading: 'Available Roles',
        points: [
          '**Organization Admin:** Full access across all projects and organizational settings.',
          '**Organization Manager:**Can manage users and projects but has limited access to organizational settings.',
          '**Project Admin:** Full access within assigned projects, including managing team members and project configurations.',
          '**Member:** Limited access within a project and can only edit test cases, test cycles, executions and associated issues.',
          '**Executive manager:** Read-only access to projects, can view test cases, executions and reports but cannot make any changes.',
        ],
        image: '/user-roles-permissions.png',
      },
    ],
  },

  'dashboard-metrics': {
    title: 'Dashboard Metrics Overview',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'The GAINSS dashboard provides a centralized view of key metrics across your projects.',
          'It helps track testing activities, defect trends, and overall project health at a glance through customizable widgets and charts.',
        ],
      },
      {
        heading: 'Key Metrics Displayed',
        points: [
          'Test case authoring progress and ownership distribution.',
          'Test case automation coverage and execution status.',
          'Defect logging statistics for quick defect detection analysis.',
          'Project-level assignment and execution statuses.',
          'Requirement coverage and jira sprint predictability.'
        ],
      },
      {
        heading: 'Using Dashboard Filters',
        steps: [
          'Apply date filters to refine the metrics shown based on a specific time range.',
          'Filter by linked Jira projects if Jira integration is enabled.',
          'Quickly export dashboards to PDF for reporting purposes.',
        ],
      },
    ],
  },

  'test-design-metrics': {
    title: 'Test Design Metrics',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'GAINSS captures detailed metrics about test design activities within your project.',
          'Track how many test cases are authored, assigned, and covered across different modules and technology layers.',
        ],
      },
      {
        heading: 'Key Metrics Displayed',
        points: [
          'Test case pyramid showing the distribution of test cases across different technology layers.',
          'Test cases created per author to identify contribution trends.',
          'Test coverage by module to ensure all areas of the application are adequately tested.',
        ],
      },
      {
        heading: 'Benefits',
        points: [
          'Improves visibility into test design progress.',
          'Helps prioritize areas needing additional test coverage.',
          'Promotes balanced test writing among team members.',
        ],
      },
    ],
  },

  'test-execution-metrics': {
    title: 'Test Execution Metrics',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'GAINSS provides real-time insights into test cycle execution activities.',
          'Visualize execution trends, pass/fail rates, and execution ownership to monitor testing efficiency.',
        ],
      },
      {
        heading: 'Key Metrics Displayed',
        points: [
          'Executions per user to understand workload distribution.',
          'Test case pass/fail rate trends during test cycles.',
          'Execution distribution based on manual vs. automated test types.',
          'Test cycles broken down by the number of iterations and duration.',
        ],
      },
      {
        heading: 'Benefits',
        points: [
          'Identifies bottlenecks and slow test cycles.',
          'Highlights test cycle health based on execution outcomes.',
          'Encourages faster and more reliable test cycle completions.',
        ],
      },
    ],
  },

  'test-automation-metrics': {
    title: 'Test Automation Metrics',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'Automation metrics in GAINSS allow you to track the progress and effectiveness of automated testing.',
          'Analyze weekly automation trends and understand the growth rate of your automation suite.',
        ],
      },
      {
        heading: 'Key Metrics Displayed',
        points: [
          'Automation progress by week to visualize how fast automation coverage is growing.',
          'Compare manual and automated executions over time.',
        ],
      },
      {
        heading: 'Benefits',
        points: [
          'Helps track return on investment (ROI) from test automation initiatives.',
          'Identifies gaps where additional automation is required.',
        ],
      },
    ],
  },

  'defects-metrics': {
    title: 'Defects Metrics',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'The Defects dashboard in GAINSS highlights critical insights into defect logging and resolution trends.',
          'Use these insights to improve product quality and identify high-risk areas early.',
        ],
      },
      {
        heading: 'Key Metrics Displayed',
        points: [
          'Defects associated with test cycles.',
          'Overall defect counts categorized by severity, status, and type.',
          'Defect detection rate based on test cycles and execution activity.',
          'Issues logged per status for custom issue types if integrated with Jira.',
        ],
      },
      {
        heading: 'Benefits',
        points: [
          'Enables proactive defect management strategies.',
          'Improves communication between QA and development teams.',
          'Provides early warnings about potential release risks.',
        ],
      },
    ],
  },
  'add-test-suite': {
    title: 'Add a Test Suite',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'A Test Suite is a collection of related test cases grouped together based on a common feature, functionality, or testing objective.',
          'In GAINSS, you must create at least one test suite before adding test cases to your project. This ensures that your test cases are organized and easily manageable.',
          'Test Suites are used to add test cases to a test cycle and execute them.',
        ],
      },
      {
        heading: 'Steps to Create a Test Suite',
        steps: [
          'Navigate to the **Test Suites** page in your project.',
          'Click on **+ New Test Suite** to begin the creation process.',
          'In the **Add Test Suite** modal, fill in all required details including the suite name, description, and any optional fields.',
          'If **Platforms & Coverage** (Execution Scope) is defined for your project, you must select one or more platforms for the test suite.',
          'After filling out the necessary information, click **Save** to create the test suite.',
        ],
        image: 'add-test-suite.png',
      },
    ],
    note: '**Note:** A Test Suite is mandatory for creating test cases. Ensure that at least one test suite exists before proceeding to test case creation. If platforms are required in your project configuration, you must select platforms while creating the test suite.',
  },
  'test-suite-actions': {
    title: 'Test Suite Actions',
    sections: [
      {
        heading: 'Manage Test Cases',
        paragraphs: [
          'View all test cases associated with a specific test suite. This provides a detailed list of individual test cases along with their descriptions, execution status, and other relevant data.',
          'Use this feature to review, update, or validate that all necessary test cases are properly defined before executing the suite.',
        ],
        image: '/manage-tc-in-test-suite.png',
      },
      {
        heading: 'Suspend Test Suite',
        paragraphs: [
          'Suspending a test suite temporarily prevents it from being added to any test cycle or executed.',
          'While suspended, you cannot add new test cases to the suite.',
          'This feature is useful when the suite needs to be paused due to changes in testing requirements, configuration issues, or review processes.',
          'Suspended suites can be restored at any time to resume updates and execution.',
        ],
      },
      {
        heading: 'Test Suite Metrics',
        paragraphs: [
          'The Test Suite Metrics section provides insights into the overall execution status and quality of the test suite.',
        ],
        points: [
          '**Execution Percentage:** Shows the percentage of test cases executed out of the total test cases planned for the suite.',
          '**Pass Percentage:** Indicates the percentage of executed test cases that have successfully passed.',
          '**Last Executed:** Displays the date and time when the test suite was last executed.',
          '**Country:** Displays the countries associated with the particular test suite.'
        ],
        image: '/test-suite-table.png',
      },
    ],
  },
  'add-test-cases-in-test-suites': {
    title: 'Add Test Cases in Test Suites',
    sections: [
      {
        heading: 'Add Test Cases While Creating or Editing Test Cases',
        paragraphs: [
          'You can link a test case to one or more test suites directly during the creation or editing of a test case.',
        ],
        steps: [
          'Navigate to the **Create Test Case** or **Edit Test Case** screens.',
          'In the form, locate the **Test Suite** field.',
          'Select one or more test suites from the dropdown list. Multiple selections are allowed.',
          'Click **Save**. The test case will now be associated with all selected test suites.',
        ],
      },
      {
        heading: 'Add Test Cases Using Manage Test Cases in a Test Suite',
        paragraphs: [
          'You can also manage and add existing test cases to a test suite from the Test Suites table.',
        ],
        steps: [
          'Navigate to the **Test Suites** page.',
          'Locate the test suite where you want to add test cases and click on **Manage Test Cases**.',
          'In the modal, click the **Add Test Cases** button.',
          'A list of available test cases will appear. You can use filters such as **Technology Layer** and **Status** to narrow down the results.',
          'Select one or more test cases you want to add to the suite.',
          'Click **Add** to link the selected test cases to the test suite.',
        ],
        image: '/add-tc-in-suite.png',
      },
      {
        heading: 'Important Notes',
        points: [
          'A single test case can be linked to multiple test suites.',
          'Test cases already associated with a test suite will not appear again in the "Add Test Cases" list for that suite.',
          'Archived test cases cannot be added to a test suite.',
        ],
      },
    ],
  },
  'add-test-case': {
    title: 'Create a Test Case',
    image: '/add-test-case-1.png',
    sections: [
      {
        heading: 'Steps to Create a Test Case',
        steps: [
          'Navigate to the **Test Cases** main page.',
          'Click the **+ New Test Case** button to open the Create Test Case form.',
          'Fill in the required fields like **Description**, **Technology Layer**, and **Test Suite**. Status and Priority are set to default values.',
          'Optionally, fill in fields like **Objective**, **Pre-condition**, **Module**, **Labels**, **Countries**, and whether the test case is automated.',
          'Users can add automation details by selecting whether the test case is a **Candidate for Automation**. By default, this is set to **No**.',
          'If **Candidate for Automation = No**, selecting a **Reason for Not Automating (Category and Subcategory)** is optional.',
          'If **Candidate for Automation = Yes**, you may optionally fill in **Automation Status** and **Is Automated**.',
          'If **Candidate for Automation = Yes** and **Is Automated = No**, selecting a **Reason for Not Automating (Category and Subcategory)** becomes mandatory.',
          'If **Is Automated = Yes**, you must provide the **Automation Method**, **Automated By**, and the **Automation Country**. (Automation Method and Automated By are mandatory.)',
          'Select one or more **Test Suites** to associate the test case with. A single test case can belong to multiple test suites.',
          'Click **Create** to save the test case.',
          'You will be redirected to the **Test Steps** tab where you can add detailed test steps.',
        ],
        image: '/add-test-case-2.png',
      },

      {
        heading: 'Adding Test Steps',
        steps: [
          'In the **Test Steps** tab, add individual steps by specifying:',
          '- **Step Description**: What the user must perform.',
          '- **Test Data**: Any input values or conditions required for execution.',
          '- **Expected Result**: The expected outcome after performing the step.',
          'You can edit or delete test steps after adding them, if necessary.',
        ],
        image: '/add-test-steps.png',
        note: 'Each field has a maximum limit of 2000 characters.',
      },
      {
        heading: 'Additional Information Tabs',
        paragraphs: [
          'After creating the basic test case, you can manage additional details:',
        ],
        steps: [
          '**Traceability**: Link the test case to Jira issues or add URLs for better traceability. You can either search for existing Jira issues or create a new one directly from this tab.',
          '**Execution**: View the historical results of test executions associated with this test case.',
          '**Attachments**: Upload supporting files or documents relevant to the test case.',
          '**Comments**: Add notes, clarifications, or discussions regarding the test case.',
        ],
      },
    ],
    note: '**Note**: Before you create a test case, ensure that at least one test suite has been created. Test suites are mandatory to organize and manage your test cases effectively.',
  },
  'import-export-test-cases': {
    title: 'Import and Export Test Cases',
    sections: [
      {
        heading: 'Import Test Cases',
        paragraphs: [
          'GAINSS provides an easy-to-use import wizard to bring in test cases from Comma-Separated Values (CSV) files. The wizard allows for quick and accurate mapping of your existing test case data into the system.',
        ],
      },
      {
        heading: 'Steps to Import Test Cases',
        steps: [
          'On the **Test Cases** main page, click the **Import** button.',
          'Upload your CSV file either by dragging and dropping it into the designated area or by clicking **Choose File** and selecting it manually.',
          'Specify the delimiter used in your CSV file (e.g., comma, semicolon).',
          'Click **Next** to move to the set value.',
          'Choose a technology layer and test suites for the imported test cases.',
          'Map the columns from your CSV file to the corresponding GAINSS fields. Columns that are not mapped will be ignored.',
          'Click **Next** and then **Import** to finalize and upload your test cases.',
        ],
        image: '/import-test-cases-1.png',
      },
      {
        heading: 'Notes',
        paragraphs: [
          'If your data is currently in Excel or Numbers, export it to CSV format before importing.',
          'Excel will create one CSV file per sheet. Make sure to use the correct file.',
          'Default values will be assigned for Status and Priority',
          'You can include fields like **Description**, **Objective**, **Pre-condition** and **Test Steps** during the import.',
        ],
        image: '/import-test-cases-2.png',
      },
      {
        heading: 'Export Test Cases',
        paragraphs: [
          'GAINSS allows you to export your test cases to a CSV file for reporting, sharing, or offline analysis. You can choose to export specific test cases or the entire list.',
        ],
      },
      {
        heading: 'Steps to Export Test Cases',
        steps: [
          'On the **Test Cases** main page, select the test cases you want to export by checking the boxes next to them.',
          'Click on the **More** dropdown menu located at the top right of the test case list.',
          'Choose **Export to CSV** from the dropdown options.',
          'If specific test cases are selected, a popup will appear showing the details of the selected test cases. Confirm your selection.',
          'If no test cases are selected, all available test cases will be exported automatically.',
          'After confirmation, the CSV file will be generated and downloaded to your local storage.',
        ],
      },
    ],
  },
  'test-case-actions': {
    title: 'Test Case Actions',
    image: '/test-case-actions-1.png',
    sections: [
      {
        heading: 'Customize Table View',
        paragraphs: [
          'The Configure Columns option allows users to control which fields are visible in the Test Case table, helping them focus on the most relevant information.',
          'Some system-defined fields may be locked and cannot be unchecked.',
        ],
        steps: [
          "Click the Settings icon in the Test Case table.",
          "Select or deselect the columns you want to show or hide.",
          "The table will update based on your selection.",
        ],
        image: '/configure-columns-1.png',
      },

      {
        heading: 'Clone Test Cases',
        paragraphs: [
          'Cloning a test case creates an exact duplicate of the original, including all the test steps but excluding executions, comments, attachments, and defects.',
        ],
        steps: [
          'Select the test cases you wish to clone.',
          'Click the <strong>Clone</strong> button.',
          'The duplicated test cases will appear with <strong>(CLONE)</strong> appended to their titles.',
          'Edit the title of the cloned test cases if necessary to better fit your organization.',
        ],
      },
      {
        heading: 'Archive Test Cases',
        paragraphs: [
          'Archiving test cases helps maintain an organized and streamlined test library by removing test cases that are not currently in use.',
          'Archived test cases are <strong>removed</strong> from all associated test suites and marked as <strong>ARCHIVED</strong>.',
          'They cannot be linked to test cycles or executed until unarchived.',
        ],
        steps: [
          'Select the test cases you wish to archive.',
          'Click the <strong>Archive</strong> button.',
          'The selected test cases will be moved to the <strong>Archived Test Cases</strong> section and removed from active suites.',
        ],
        image: '/test-case-actions-2.png',
      },
      {
        heading: 'Unarchive Test Cases',
        paragraphs: [
          'Unarchiving restores a test case back to the active test library with its status reset to <strong>Draft</strong>.',
          'Unarchived test cases are automatically reassigned to the original test suite(s) or test cycle(s).',
        ],
        steps: [
          'Go to the <strong>Archived Test Cases</strong> section.',
          'Select the test cases you want to restore.',
          'Click the <strong>Unarchive</strong> button.',
        ],
      },
      {
        heading: 'Filter Test Cases',
        paragraphs: [
          'Quickly locate specific test cases by applying filters based on various criteria like status, priority, author, module, technology layer, test suites and executions scope etc.',
        ],
        steps: [
          'Click the <strong>Filter</strong> button in the Test Cases view.',
          'A modal window will appear with multiple filtering options.',
          'Select your desired filters from the available options.',
          'Click <strong>Apply</strong> to filter the results.',
          'Active filters will be displayed just below the search bar for easy tracking.',
        ],
        image: '/filter-testcases-1.png',
      },
    ],
  },
  'test-case-bulk-edit': {
    title: 'Bulk Edit Test Cases',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'Bulk editing allows you to update multiple test cases at once, making it easier to manage test cases.',
          'You can update attributes such as **Status**, **Priority**, **Labels**, **Country**, **Module**, **Technology Layer**, **Test Suites**, and **automation details**.',
        ],
      },
      {
        heading: 'How to Use Bulk Edit',
        image: '/bulkEditTestCase.png',
        steps: [
          'Select one or more test cases from the test case list view.',
          'Click the **Bulk Edit** button to open the Bulk Edit modal.',
          'In the modal, choose which fields you want to edit by selecting them from the **"Select Fields to Edit"** dropdown.',
          'For each selected field, fill in the corresponding value. Fields can include:',
          '**Status**: Choose a new status (e.g., *Draft*, *In Review*, *Approved*). If *In Review* is selected and the review workflow is enabled, a **Reviewer** must also be selected.',
          '**Priority**, **Module**, **Technology Layer**: Select from predefined dropdown options.',
          '**Labels** and **Test Suites**: Select multiple values. You can choose to either *Append* or *Replace* existing values.',
          '**Country**: Select from the list of countries available in the project configuration. Multiple countries can be added. These countries are directly linked to the test case.',

          '**Is Automated**: Select *Yes* or *No*. If *Yes*, you must specify who automated it and the automation method and automation country for which country you want to automate for .',

          '**Automation Country**: Choose countries where this automation is applicable. You can select from the test case’s existing countries. If you select a country that is not already part of the test case country list, it will be added only after you click the **Update** button.',

          'Click **Apply** to confirm your changes.',
        ],
      },
      {
        heading: 'Additional Notes',
        paragraphs: [
          '- When changing the **Technology Layer** or **Test Suites**, the system will warn you if there are any compatibility issues.',
          '- If you select the **Archived** status, the test case will be removed from all test suites and can no longer be executed or linked to test cycles.',
        ],
      },
    ],
    note: '**Note**: You must select at least one test case and one editable field to enable bulk editing.',
  },

  'delete-test-cases': {
    title: 'Deleting Test Cases',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          'To maintain a clean and organized test library, you may need to delete outdated or unnecessary test cases.',
          'Before you can delete a test case, it must first be <strong>archived</strong>.',
        ],
        note: 'Test cases that are part of a test cycle or have dependencies cannot be deleted. Only one page of test cases can be deleted at a time to minimize impact on your instance.',
      },
      {
        heading: 'Steps to Delete a Test Case',
        steps: [
          'Navigate to the <strong>Archived Test Cases</strong> section.',
          'Select the test cases you wish to delete.',
          'Click the <strong>Delete</strong> button to initiate the deletion process.',
        ],
      },
      {
        heading: 'Confirmation & Finalization',
        paragraphs: [
          'A confirmation prompt will appear, warning you that deleting the test case(s) will permanently remove all related data, including test steps, executions, comments, and attachments.',
          'Once confirmed, the deletion process may take some time, depending on the number of test cases, to ensure complete removal of all associated records.',
        ],
        image: '/delete-test-case.png',
      },
    ],
    note: '**Important**: This action <strong>cannot be undone</strong>. Ensure that you have reviewed all associated data before confirming the deletion.',
  },

  'add-test-cycle': {
    title: 'Add a Test Cycle',
    description:
      'This guide explains how to create a test cycle from the Cycles view and provides key details about the process.',

    sections: [
      {
        heading: 'Steps to Create a Test Cycle',
        steps: [
          'On the **Test Cycles** main page, click **+New Test Cycle**.',
          'The **Create Test Cycle** modal will appear.',
          'Fill in the required fields.',
          'Click **Create**.',
          'You will be redirected to the **Test Cases** tab of the newly created test cycle where you can add and configure test cases.',
        ],
        image: 'create-test-cycle-1.png',
      },
      {
        heading: 'Post-Creation Configuration',
        image: '/post-test-cycle.png',
        paragraphs: [
          'Once the test cycle is created, you can configure it further using the following tabs:',
        ],
        points: [
          '**Details**: Review and update the test cycle’s details.',
          '**Executions**: View and manage the execution status of test cases within the cycle.',
          '**Test Cases**: Add test cases, assign environments and testers.',
          '**Linked Issues**: View issues logged during test execution.',
          '**Attachments**: Upload relevant files for reference.',
          '**Comments**: Add notes or discussions related to the cycle.',
        ],
      },
    ],

    note: 'If Platforms in **Execution Scope** are defined in your project configuration, you must select one or more platforms when creating the test cycle.',
    bottomNote:
      '**Note:** Only approved test cases can be added to a test cycle. If a test case is not approved, it cannot be executed. ',
  },
  'add-testcaseto-testcycle': {
    title: 'Add Test Case to Test Cycle',
    description:
      'This guide explains how to add approved test cases to an existing test cycle and assign execution settings and testers.',

    sections: [
      {
        heading: 'Adding Test Cases to a Test Cycle',
        paragraphs: [
          'You can add approved test cases to the test cycle by selecting the relevant test suites. The platform allows you to add all test cases from a suite at once or review and choose specific test cases before adding them.',
          'Click on **+ Test Cases** on the Test Cases tab to open the test suite selection modal.',
          'Select the required test suite(s). Use the **+ icon** to quickly add all approved test cases from a suite, or proceed to choose and add only the specific test cases you want.',
          'You can also click the **Next** button to review the test cases in the selected suite and select only the desired ones before finalizing.'
        ],
        image: 'adding-tc-to-cycle.png'
      },

      {
        heading: 'Test Case Selection Modal',
        paragraphs: [
          'The modal provides a structured view of the selected test suites and their test cases. You can review, filter, and select individual test cases before adding them to the test cycle.',
          'This ensures flexibility by allowing you to either bulk-add or pick specific test cases as needed.'
        ],
        image: 'adding-tc-to-cycle-modal.png'
      },

      {
        heading: 'Assigning Testers',
        paragraphs: [
          'You can assign a tester to each test case individually or apply tester assignments in bulk for all test cases included in the cycle.'
        ]
      },
      {
        heading: 'Next Steps',
        paragraphs: [
          'Once the test cases are added, proceed to the **Execution** page to configure execution details and begin running the test cycle.'
        ]
      }
    ],

    note: 'Only approved test cases can be added to a test cycle.'
  },

  'execute-test-cases': {
    title: 'Execute Test Cases',
    description:
      'This is where testers carry out test executions, track results, and provide details about outcomes, attachments, comments, and issues.',

    sections: [
      {
        heading: 'Execution and Test Case Details',
        paragraphs: [
          'This section allows the details of the test case and execution, including the tester assigned, environment, creator, iteration and time taken.',
          'A dropdown is available to filter executions. Users can select options such as **All Executions**, **Assigned to Me**, **Not Executed Test Cases**, or **Archived Executions**. Based on the selected filter, only the relevant test cases execuations will be displayed.',
        ],
        image: 'execute-test-cases.png',
      },
      {
        heading: 'Test Steps',
        paragraphs: [
          'Lists the steps to be executed as part of the test case. Each step can be assigned a status, and observations or expected results can be reviewed or edited.',
          'Users can also bulk-edit test steps by selecting two or more steps and updating their status in a single action.'
        ],
        points: [
          'Steps are read-only if the test cycle is completed.',
          'Failed steps are important for failure analysis and reporting.',
          'If any single test step fails, users can directly link that failure to a Jira issue as the Link Issue modal will automatically open.'
        ],
        image: "bulk-edit-test-steps.png"
      },
      {
        heading: 'Failure Analysis (Conditional)',
        paragraphs: [
          'If a test execution fails, this section allows you to perform root cause analysis by categorizing the failure and providing supporting notes or observations.',
        ],
      },
      {
        heading: 'Update Test Scripts Alert (when steps are modified)',
        paragraphs: [
          'This alert appears if any test steps have been modified since the last execution. It prompts the user to update the test scripts to ensure they are in sync with the latest changes in the test case.',
        ],
      },
      {
        heading: 'Comments',
        paragraphs: [
          'Facilitates collaboration and discussion among team members about the specific test execution. Users can log observations, communicate with testers, or document test evidence.',
        ],
      },
      {
        heading: 'Linked Issues',
        image: '/test-step-fail.png',
        paragraphs: [
          'Allows testers to link issues discovered during execution to the current test case execution. Helps maintain traceability between failures and defects.',
          'Suggestions for linking issues are shown if the test step execution **Fails**.',
        ],
      },
      {
        heading: 'Attachments',
        paragraphs: [
          'Users can upload relevant screenshots, logs, or other documents that support or validate the outcome of the test execution.',
        ],
      },
      {
        heading: 'Iteration Insights',
        image: '/iteration-insights.png',
        paragraphs: [
          'This section provides a visual summary of test execution progress for the selected iteration.',
          'It displays the total number of test cases along with their execution status, including **Not Executed**, **Passed**, **Failed**, **Blocked**, and **In Progress**.',
          'A graphical chart helps users quickly understand the overall execution health and identify areas that need attention.'
        ]
      }
    ],
  },

  'test-cycle-actions': {
    title: 'More Test Cycle Actions',
    description:
      'Beyond creation and execution, test cycles support additional actions like cloning and filtering. These features help streamline management and reuse of test cases across multiple cycles.',
    image: 'test-cycle-actions.png',
    sections: [
      {
        heading: 'Clone Test Cycle',
        paragraphs: [
          'Cloning a test cycle allows you to reuse test cases without carrying over execution history or associated data, helping you start fresh with the same structure.',
        ],
        steps: [
          'Navigate to the **Test Cycles** page and select the test cycle you wish to clone.',
          'Click the **Clone** button.',
          'If the test cycle is still active, a prompt will appear offering you the option to complete the current cycle or proceed with a different configuration.',
          'You can provide a new **purpose** and **type** for the cloned cycle.',
          'The cloned test cycle will appear in the table with the label **(CLONE)** in its title.',
          'Click the cloned cycle to rename or configure it further.',
        ],
        note: '**Note:** Cloning does **not** carry over executions, comments, attachments, or linked issues. Only the test case structure is retained.',
      },
      {
        heading: 'Filter Test Cycles',
        image: '/test-cycle-filter.png',
        paragraphs: [
          'Use filters to quickly locate specific test cycles based on various criteria..',
        ],
        steps: [
          'Go to the **Test Cycles** view.',
          'Click the **Filter** button.',
          'In the modal that appears, select the filters you want to apply.',
          'Click **Apply** to view filtered results.',
          'Applied filters will be shown below the search bar for reference.',
        ],
      },
    ],
  },

  'delete-test-cycle': {
    title: 'Deleting a Test Cycle',
    description:
      'To maintain a clean and organized test environment, you can delete outdated or unnecessary test cycles. Deleting a test cycle removes it along with all its associated data.',

    sections: [
      {
        heading: 'Steps to Delete a Test Cycle',
        steps: [
          'Navigate to the **Test Cycles** view.',
          'Select the test cycle you want to delete.',
          'Click the **Delete** button.',
          'A **confirmation box** will appear, explaining the consequences of deletion.',
          'Review the information carefully and click **Confirm** if you wish to proceed.',
        ],
      },
    ],
    note: '**Note:** This action is **permanent and cannot be undone**. Make sure to review all associated data before confirming deletion.',

    bottomNote:
      '**Warning:** Deleting a test cycle will permanently remove all linked test executions, comments, attachments, and issues associated with it.',
  },

  'test-plan-suggestions': {
    title: '**Test Plan Suggestions**',
    description:
      'The Test Plan Suggestions feature helps streamline your testing process by generating a tailored test plan using intelligent filtering and analysis. This ensures that your test cycle focuses on the most critical and relevant test cases, saving you time and effort.',
    sections: [
      {
        heading: '**How It Works**',
        steps: [
          '**Select Modules**: Choose the modules that represent the components or features of your application that need testing. These modules help scope your test plan to specific areas of interest.',
          '**Apply Filters**: Refine the test cases by filtering based on the priority of linked issues. This allows you to prioritize test cases associated with high-priority or critical issues to ensure key project areas are covered.',
          '**Review Smart Suggestions**: Based on your selections and filters, the system will suggest a list of relevant test cases. Each suggestion includes: Automation coverage, Linked issue priorities, Test case status, Associated module details.',
          '**Filter Test Cases**: Use additional filters to refine the list further. You can choose to show only automated test cases or filter by specific modules.',
          '**Select and Create Test Cycle**: Once you’ve finalized your test cases, select the ones you wish to include and proceed to create a test cycle. This cycle will be pre-populated with the selected test cases, ready for execution.',
        ],
      },
      {
        heading: '**Benefits**',
        points: [
          'Focus on high-priority, relevant test cases',
          'Save time with intelligent filtering',
          'Ensure coverage of critical project areas',
          'Quickly create comprehensive and focused test cycles',
        ],
      },
      {
        heading: '**Getting Started**',
        paragraphs: [
          'To start using Test Plan Suggestions, navigate to **Test Intelligence**, select your desired modules and issue priorities, review the suggested test cases, and create your optimized test cycle to start execution.',
        ],
      },
    ],
  },

  'configure-automation-tool': {
    title: 'Configure Jenkins or GitHub Actions with GAINSS for Automation',
    note: '**Note**: You must be an organization admin, manager or project admin to configure the automation execution tool for your project in GAINSS.',
    sections: [
      {
        heading: 'Steps to Configure Automation Execution Tool',
        steps: [
          'Navigate to **Configurations** in the project sidebar.',
          'Under the **Integration** section, click on **Automation**.',
          'Select **Execution Tool**. You can choose either **Jenkins** or **GitHub Actions**.',
        ],
      },
      {
        heading: 'Jenkins Configuration',
        steps: [
          'If you selected **Jenkins**, provide the following details:',
          '**Jenkins URL**',
          '**Username**',
          '**Auth Token**',
          'Click **Save** to verify the details and complete the integration.',
        ],
      },
      {
        heading: 'GitHub Actions Configuration',
        steps: [
          'If you selected **GitHub Actions**, provide the following details:',
          '**GitHub Repo URL**',
          '**Username**',
          '**Auth Token (Personal Access Token - PAT)**',
          'Click **Save** to verify the details and complete the integration.',
        ],
      },
    ],
  },

  'assigning-job-workflow': {
    title: 'Assign a Job or Workflow to a Test Cycle',
    sections: [
      {
        heading: 'Steps to Assign a Job or Workflow',
        steps: [
          'Navigate to the **Test Cycle** view.',
          'Ensure the **Test Cycle Type** is set to **Automation**. This is required to assign a job or workflow.',
          'Select the **Job** or **Workflow** you want to assign, depending on the execution tool configured (either **Jenkins** or **GitHub Actions**).',
          'You will see a list of available jobs or workflows fetched from your configured **Jenkins** or **GitHub** repository.',
          'Click **Save** to confirm the assignment.',
        ],
      },
      {
        heading: 'Alternative Option During Creation',
        paragraphs: [
          'You can also assign a job or workflow directly during the **Test Cycle creation** process by setting the type to **Automation** and selecting the relevant job/workflow before creating the test cycle.',
        ],
      },
    ],
  },

  'running-automation': {
    title: 'Running an Automation',
    sections: [
      {
        heading: 'Steps to Run an Automation in GAINSS',
        steps: [
          'Navigate to the **Execution** screen of the selected the Test Cycle.',
          'Select the desired **iteration** for which you want to execute the automation.',
          'Click the **Run Automation** button.',
          'The system will check the **build status** of any previous builds for the selected iteration:',
          '- If a previous build exists, it will display the relevant **status**.',
          '- If no previous build exists, you can proceed to **trigger** the job or workflow.',
          'Click **Run** to initiate the job/workflow.',
          'After successful triggering, the **popup will close** and a **success notification** will appear.',
          'If there is an existing build in progress for the selected iteration, the system will display its status as **Running**.',
          'You can also **Abort** the running build by clicking the **Abort** button in the popup.',
        ],
      },
    ],
    note: '**Note**: Ensure that the **Test Cycle Type** is set to **Automation** and that a **Job/Workflow** is assigned. Only when both conditions are met, a **Run Automation** button will be visible.',
  },

  'generate-api-token': {
    title: 'Generate API Access Token',
    sections: [
      {
        heading: 'Steps to Generate an API Access Token',
        steps: [
          'Navigate to **Configurations** in the project sidebar.',
          'Under the **Automation** section, click on **API Token**.',
          'Click on **Generate GAINSS API Token** to create a new token.',
          'The token will be valid for **90 days** from the date of creation.',
          'Click the **Click to Copy** button to copy the token.',
          'Use the copied token in your GAINSS API requests for **authorization**.',
        ],
      },
    ],
  },
  'update-execution-status': {
    title: 'Update Execution Status',
    description:
      'Use this endpoint to update the execution status and time of one or more test cases programmatically.',
    sections: [
      {
        heading: 'API Information',
        points: [
          `**API URL**: <code>${process.env.REACT_APP_API_URL}</code>`,
          '**Endpoint**: `/api/v1/projects/automation/update-test-status`',
          '**Method**: `PUT`',
        ],
      },
      {
        heading: 'Authentication & Headers',
        points: [
          '`Authorization: Bearer {GAINSS_API_TOKEN}`',
          '`Content-Type: application/json`',
        ],
        note: '**Note**: Replace `{GAINSS_API_TOKEN}` with your actual GAINSS API token.',
      },
      {
        heading: 'Payload',
        paragraphs: [
          'Use the following JSON structure to update execution status for multiple test cases within a test cycle.',
        ],
        codeBlock: {
          language: 'json',
          content: `{
    "testcases": [
      {
        "testcaseKey": "TEST_CASE_KEY_1",
        "status": "Pass",
        "executionTime": 25
      },
      {
        "testcaseKey": "TEST_CASE_KEY_2",
        "status": "Fail",
        "executionTime": 101
      }
    ],
    "iteration": "ITERATION_NAME",
    "testcycle": "TEST_CYCLE_KEY"
}`,
        },
      },
      {
        heading: 'Payload Descriptions',
        points: [
          '**`testcases`**: An array of test case objects to update. Each object must contain:',
          '`testcaseKey`: Unique key identifying the test case',
          '`status`: Execution status of the test case (e.g., `Pass`, `Fail`). You can refer to your **Project Configurations** for the list of valid status values.',
          '`executionTime`: (Optional) Time taken to execute the test case, in milliseconds.',
          '**`iteration`**: Name of the iteration (matches left panel in execution screen).',
          '**`testcycle`**: Unique key identifying the test cycle.',
        ],
      },
      {
        heading: 'Error Codes',
        points: [
          '`400` – Bad Request',
          '`401` – Unauthorized',
          '`403` – Forbidden',
          "`404` – Not Found (e.g. test cycle, iteration, or testcase doesn't exist)",
          '`500` – Internal Server Error',
        ],
      },
    ],
  },
  'create-new-iteration': {
    title: 'Create New Iteration',
    description:
      'Use this endpoint to create a new iteration and assign selected test cases to it within an existing test cycle programmatically.',
    sections: [
      {
        heading: 'API Information',
        points: [
          `**API URL**: <code>${process.env.REACT_APP_API_URL}</code>`,
          '**Endpoint**: `/api/v1/automation/create-iteration`',
          '**Method**: `POST`',
        ],
      },
      {
        heading: 'Authentication & Headers',
        points: [
          '`Authorization: Bearer {GAINSS_API_TOKEN}`',
          '`Content-Type: application/json`',
        ],
        note: '**Note**: Replace `{GAINSS_API_TOKEN}` with your actual GAINSS API token.',
      },
      {
        heading: 'Payload',
        paragraphs: [
          'Use the following JSON structure to create a new iteration and assign test cases to it.',
        ],
        codeBlock: {
          language: 'json',
          content: `{
  "testcycle": "GC-C37",
  "environment": "Staging",
  "testcaseKeys": ["GC-TC28", "GC-TC30"],
  "iteration": "Iteration Name"
  "country": "India"
}`,
        },
      },
      {
        heading: 'Payload Descriptions',
        points: [
          '**`testcycle`**: The key of the test cycle where the new iteration should be created.',
          '**`environment`**: The environment where the test cases will be executed (e.g., Staging, QA, Production). You can refer to your **Project Configurations** for the list of valid environment values.',
          '**`testcaseKeys`**: An array of test case keys to include in the new iteration.',
          '**`iteration`**: A custom name for the new iteration (must be unique within the test cycle).',
          '**`country`**: A country name must be provided as a string, and it should include only those countries that fall within the test cycle execution scope..'
        ],
      },
      {
        heading: 'Response',
        paragraphs: ['A successful response will look like this:'],
        codeBlock: {
          language: 'json',
          content: `{
  "success": true,
  "message": "New iteration and executions created successfully."
}`,
        },
      },
      {
        heading: 'Error Codes',
        points: [
          '`400` – Bad Request',
          '`401` – Unauthorized',
          '`403` – Forbidden',
          "`404` – Not Found (e.g., test cycle doesn't exist)",
          '`500` – Internal Server Error',
        ],
      },
    ],
  },

  'update-execution-step-status': {
    title: 'Update Execution Steps (Status and Actual Result)',
    description:
      'Use this endpoint to update the execution status and actual result of the individual steps within a test case execution in a specific iteration of a test cycle.',
    sections: [
      {
        heading: 'API Information',
        points: [
          `**API URL**: <code>${process.env.REACT_APP_API_URL}</code>`,
          '**Endpoint**: `/api/v1/automation/update-test-step-status`',
          '**Method**: `PUT`',
        ],
      },
      {
        heading: 'Authentication & Headers',
        points: [
          '`Authorization: Bearer {GAINSS_API_TOKEN}`',
          '`Content-Type: application/json`',
        ],
        note: '**Note**: Replace `{GAINSS_API_TOKEN}` with your actual GAINSS API token.',
      },
      {
        heading: 'Payload',
        paragraphs: [
          'Use the following JSON structure to update the status and actual result of individual test steps within a test case.',
        ],
        codeBlock: {
          language: 'json',
          content: `{
  "testcaseKey": "TL-TC42",
  "testcycle": "TL-C1",
  "iteration": "Iteration 4",
  "steps": [
    {
      "sNo": 1,
      "status": "In Progress",
      "actualResult": "Step executing"
    },
    {
      "sNo": 2,
      "status": "Fail",
      "actualResult": "Expected result not met"
    },
    {
      "sNo": 5,
      "status": "Pass",
      "actualResult": "Step executed successfully"
    }
  ]
}`,
        },
      },
      {
        heading: 'Payload Descriptions',
        points: [
          '**`testcaseKey`**: The unique key of the test case being executed.',
          '**`testcycle`**: The key of the test cycle in which the test case is being executed.',
          '**`iteration`**: The name of the iteration in which the test case is being executed.',
          '**`steps`**: An array of individual test steps to update. Each step must include:',
          '`sNo`: Step number (an integer).',
          '`status`: The status of the step (e.g., Pass, Fail, In Progress). You can refer to your **Project Configurations** for the list of valid status values.',
          '`actualResult`: The actual result of the step execution.',
        ],
      },
      {
        heading: 'Response',
        paragraphs: ['A successful response will look like this:'],
        codeBlock: {
          language: 'json',
          content: `{
  "success": true,
  "message": "Test steps updated successfully."
}`,
        },
      },
      {
        heading: 'Error Codes',
        points: [
          '`400` – Bad Request',
          '`401` – Unauthorized',
          '`403` – Forbidden',
          "`404` – Not Found (e.g. test cycle, iteration, or testcase doesn't exist)",
          '`500` – Internal Server Error',
        ],
      },
    ],
  },
  'add-execution-attachments': {
    title: 'Add Attachments and Update Execution Result',
    description:
      'Use this endpoint to upload attachments (files) and update the execution status, execution time for a test case execution in a specific iteration of a test cycle.',
    sections: [
      {
        heading: 'API Information',
        points: [
          `**API URL**: <code>${process.env.REACT_APP_API_URL}</code>`,
          '**Endpoint**: `/api/v1/automation/update-execution-result`',
          '**Method**: `POST`',
        ],
      },
      {
        heading: 'Authentication & Headers',
        points: [
          '`Authorization: Bearer {GAINSS_API_TOKEN}`',
          '`Content-Type: multipart/form-data`',
        ],
        note: '**Note**: Replace `{GAINSS_API_TOKEN}` with your actual GAINSS API token.',
      },
      {
        heading: 'Payload',
        paragraphs: [
          'Use the following **form-data** structure to update the execution status, time, and/or upload attachments (files) for a test case.',
          'The API supports four scenarios:',
          '1. **Only status provided** – Updates execution status.',
          '2. **Only executionTime provided** – Updates execution time.',
          '3. **Only files provided** – Uploads files.',
          '4. **Files + Execution Update** – Updates execution status and time, and uploads files.',
        ],
        codeBlock: {
          language: 'json',
          content: `{
  "testcaseKey": "TL-TC40",
  "iteration": "Iteration 1",
  "testcycle": "TL-C2",
  "files": "File (Choose file)",
  "executionTime": 180000,
  "status": "Pass"
}`,
        },
      },
      {
        heading: 'Payload Descriptions',
        points: [
          '**`testcaseKey`**: The unique key of the test case being executed.',
          '**`iteration`**: The name of the iteration in which the test case is being executed.',
          '**`testcycle`**: The key of the test cycle in which the test case is being executed.',
          '**`files`**: The file(s) being uploaded as attachments. This field should be used when uploading files (can accept multiple files).',
          'Allowed file types: jpg, png, pdf, csv',
          '**`executionTime`**: The time taken to execute the test case, in milliseconds (ms).',
          '**`status`**: The execution final status of the test case. You can refer to your **Project Configurations** for the list of valid status values.',
        ],
      },
      {
        heading: 'Response',
        paragraphs: ['A successful response will look like this:'],
        codeBlock: {
          language: 'json',
          content: `{
  "success": true,
  "message": "Execution status, time, and/or files uploaded successfully."
}`,
        },
      },
      {
        heading: 'Error Codes',
        points: [
          '`400` – Bad Request (e.g., missing required fields, invalid format)',
          '`401` – Unauthorized (e.g., invalid or missing token)',
          '`403` – Forbidden',
          "`404` – Not Found (e.g., test cycle, iteration, or testcase doesn't exist)",
          '`500` – Internal Server Error',
        ],
      },
    ],
  },

  "start-edit-iteration": {
    "title": "Start a New Iteration",
    "sections": [
      {
        "heading": "Overview",
        "paragraphs": [
          "A new iteration groups selected test cases to be executed in a specific environment. With the updated flow, permissions determine who can assign test cases and who can simply select and execute them.",
          "Organization Admins and Organization Managers have extended capabilities, including selecting test cases for the team and assigning testers in bulk. Team Members can only select test cases and start the iteration—they cannot assign testers."
        ]
      },
      {
        "heading": "Steps to Start a New Iteration",
        "steps": [
          "Click **Start New Iteration** to open the iteration setup modal.",
          "Select the **Test Cases** you want to include in the iteration. A dedicated 'Select Test Cases' view allows searching and choosing the required test cases.",
          "If you are an **Organization Admin** or **Organization Manager**, you can bulk-assign testers by selecting one or more test cases and applying the assignment.",
          "Team Members can only select test cases; they cannot assign or reassign any tester.",
          "Enter a unique **Iteration Name**.",
          "Select the required **Environment** and complete all mandatory fields.",
          "Click **Start** to create the iteration."
        ],
        image: "start-new-iteration.png"
      },
      {
        "heading": "Edit Iteration",
        "paragraphs": [
          "The Edit Iteration modal allows authorized users to modify iteration details, update testers, and manage assignments after the iteration has been created."
        ],
        "points": [
          "Organization Admins and Organization Managers can bulk-edit tester assignments directly from the Edit Iteration modal.",
          "Team Members do not have permission to change tester assignments. They can only view assigned test cases and iteration details."
        ],
        image: "edit-iteration-modal.png"
      },
      {
        "heading": "Important Notes",
        "points": [
          "At least one test case and one environment must be selected to start a new iteration.",
          "Only approved test cases will be available for selection.",
          "Permissions strictly control assignment actions—bulk assignment and reassignment are restricted to Organization Admins and Organization Managers."
        ]
      }
    ]
  }

};
